// Which brand this copy of the app runs under (ADR 0034), read from the host it was opened on: the
// product is MyBooks on mybooks.click and Xone on xone.biz and xonebusiness.com. The name in the app's
// own wording, its logo, favicon and page title follow it. brand.test.ts checks the domains against
// the brand seeds in packages/brands.
//
// A host that belongs to no brand (localhost, workers.dev) shows the fallback brand, as the site
// does; ?brand=<id> picks another one there and is remembered, so either can be tried locally.

export type AppBrandId = 'xone' | 'mybooks';
export type AppBrand = {
  id: AppBrandId;
  /** The product's name in the app's own sentences ("Xone sets up…"). */
  name: string;
  /** The domains whose site and app hosts (dev- and stage- ones included) belong to this brand. */
  domains: string[];
};

export const APP_BRANDS: Record<AppBrandId, AppBrand> = {
  xone: { id: 'xone', name: 'Xone', domains: ['xone.biz', 'xonebusiness.com'] },
  mybooks: { id: 'mybooks', name: 'MyBooks', domains: ['mybooks.click'] },
};

/** packages/brands' FALLBACK_BRAND_ID is xone-eu. */
const FALLBACK: AppBrandId = 'xone';
const OVERRIDE_KEY = 'mybooks.brand';

const isBrandId = (v: unknown): v is AppBrandId => v === 'xone' || v === 'mybooks';

/** The brand a host belongs to, or null when it belongs to none. */
export function brandOfHost(host: string): AppBrand | null {
  const h = host.trim().toLowerCase().replace(/:\d+$/, '').replace(/\.$/, '');
  return Object.values(APP_BRANDS).find((b) => b.domains.some((d) => h === d || h.endsWith(`.${d}`))) ?? null;
}

function current(): AppBrand {
  if (typeof window === 'undefined') return APP_BRANDS[FALLBACK];
  const own = brandOfHost(window.location.hostname);
  if (own) return own;
  try {
    const asked = new URLSearchParams(window.location.search).get('brand');
    if (isBrandId(asked)) localStorage.setItem(OVERRIDE_KEY, asked);
    const saved = localStorage.getItem(OVERRIDE_KEY);
    if (isBrandId(saved)) return APP_BRANDS[saved];
  } catch { /* storage blocked: the fallback brand */ }
  return APP_BRANDS[FALLBACK];
}

export const BRAND: AppBrand = current();
/** The product name to use in the app's wording. */
export const APP_NAME = BRAND.name;

/** The tab title and favicon for the brand; index.html carries none, so no other brand shows first. */
export function installBrand() {
  document.title = APP_NAME;
  const icons: [string, string, string?][] = BRAND.id === 'xone'
    ? [['/favicon.svg', 'image/svg+xml']]
    : [['/favicon-light.png', 'image/png', '(prefers-color-scheme: light)'], ['/favicon-dark.png', 'image/png', '(prefers-color-scheme: dark)']];
  for (const [href, type, media] of icons) {
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = href;
    link.type = type;
    if (media) link.media = media;
    document.head.appendChild(link);
  }
}
